import { useEffect, useMemo, useState } from 'react'
import { Button, Chip, Eyebrow, Select } from '@/ui'
import { cls } from '@/ui/cls'
import { fmtDate, fmtNum } from '@/shared/lib/format'
import {
  type FieldDef, type PersonRef, type PicklistDef, type PipelineDef,
  useConfigUsers, useDealWrites, useProcessConfig,
} from '@/shared/api/processConfig'
import { type Deal, useCompanyDeals, useOpenDeal, useUpdateDeal } from '@/shared/api/deals'
import { READ_ONLY_TITLE, useCanEdit } from '@/shared/write-access'
import { Field } from './DetailField'
import {
  type Draft, customPatch, fieldsFor, leadDeal, optionsFor, pipelinesWithOpenDeal, toDraft,
} from './customFields'

/**
 * The lead's deals, and the custom fields of the one the card edits.
 *
 * Renders nothing until the API reports dealWrites for this viewer: before
 * write_path is new a second deal or a custom value has no old row to be
 * copied back into, and until phase7 lets the viewer in the API refuses the
 * write.
 */
export default function DealsSection({ companyId }: { companyId: string }) {
  const dealWrites = useDealWrites()
  const { data: config } = useProcessConfig(dealWrites)
  const { data: dealData, error } = useCompanyDeals(companyId, dealWrites)
  const fields = useMemo(() => fieldsFor(config?.fields ?? [], 'deal'), [config])
  const needsPeople = fields.some(f => f.type === 'user')
  const { data: people } = useConfigUsers(dealWrites && needsPeople)

  if (!dealWrites) return null
  if (error) {
    return (
      <section>
        <Eyebrow as="h4" size="sm" gap="md">Affärer</Eyebrow>
        <p className="text-xs text-[var(--color-danger)] m-0">Kunde inte hämta affärer: {(error as Error).message}</p>
      </section>
    )
  }
  if (!config || !dealData) return null

  const deals = dealData.deals
  const deal = leadDeal(deals)
  const sales = config.pipelines.find(p => p.isDefault)?.name ?? 'standardpipelinen'

  return (
    <>
      {fields.length > 0 && (
        <section>
          <Eyebrow as="h4" size="sm" gap="md">Affärsfält</Eyebrow>
          {deal ? (
            <DealFields
              key={deal.id}
              deal={deal}
              fields={fields}
              picklists={config.picklists}
              people={people?.users ?? []}
            />
          ) : (
            <p className="text-xs text-ink-500 m-0">
              Ingen affär i {sales}. Öppna en nedan för att registrera dess fält.
            </p>
          )}
        </section>
      )}
      <section>
        <Eyebrow as="h4" size="sm" gap="md">Affärer</Eyebrow>
        <DealList deals={deals} />
        <OpenDeal companyId={companyId} deals={deals} pipelines={config.pipelines} />
      </section>
    </>
  )
}

function DealFields({ deal, fields, picklists, people }: {
  deal: Deal
  fields: FieldDef[]
  picklists: PicklistDef[]
  people: PersonRef[]
}) {
  const update = useUpdateDeal()
  const canEdit = useCanEdit()
  const initial = useMemo(
    () => Object.fromEntries(fields.map(f => [f.key, toDraft(f, deal.custom[f.key])])),
    [fields, deal.custom],
  )
  const [drafts, setDrafts] = useState<Record<string, Draft>>(initial)
  // A save returns the server's coerced values; show those, not what was typed.
  useEffect(() => setDrafts(initial), [initial])

  const patch = customPatch(fields, deal.custom, drafts)
  const dirty = Object.keys(patch).length > 0
  const set = (key: string, value: Draft) => setDrafts(d => ({ ...d, [key]: value }))

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        if (dirty && canEdit) update.mutate({ id: deal.id, patch: { custom: patch } })
      }}
    >
      {fields.map(f => (
        <Field key={f.key} label={f.required ? `${f.label} *` : f.label}>
          <CustomFieldInput
            field={f}
            draft={drafts[f.key]}
            onChange={v => set(f.key, v)}
            picklists={picklists}
            stored={deal.custom[f.key]}
            people={people}
            disabled={update.isPending || !canEdit}
          />
        </Field>
      ))}
      <div className="flex items-center gap-3 pt-3">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!dirty || update.isPending || !canEdit}
          title={canEdit ? undefined : READ_ONLY_TITLE}
        >
          {update.isPending ? 'Sparar…' : 'Spara fält'}
        </Button>
        {dirty && !update.isPending && (
          <button
            type="button"
            className="bg-transparent border-0 p-0 text-xs text-ink-500 hover:text-ink-1000 cursor-pointer"
            onClick={() => { setDrafts(initial); update.reset() }}
          >
            Återställ
          </button>
        )}
        {update.error && (
          <span role="alert" className="text-xs text-[var(--color-danger)]">{(update.error as Error).message}</span>
        )}
      </div>
    </form>
  )
}

function CustomFieldInput({ field, draft, onChange, picklists, stored, people, disabled }: {
  field: FieldDef
  draft: Draft
  onChange: (value: Draft) => void
  picklists: PicklistDef[]
  stored: Deal['custom'][string] | undefined
  people: PersonRef[]
  disabled: boolean
}) {
  const text = typeof draft === 'string' ? draft : ''
  const keep = Array.isArray(stored) ? stored : typeof stored === 'string' ? [stored] : []
  switch (field.type) {
    case 'bool':
      return (
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="w-auto"
            checked={draft === true}
            disabled={disabled}
            onChange={e => onChange(e.target.checked)}
          />
          <span>{draft === true ? 'Ja' : 'Nej'}</span>
        </label>
      )
    case 'select':
      return (
        <select value={text} disabled={disabled} onChange={e => onChange(e.target.value)} aria-label={field.label}>
          <option value="">— inte angivet —</option>
          {optionsFor(picklists, field.picklistId, keep).map(o => (
            <option key={o.id} value={o.value}>{o.label}</option>
          ))}
        </select>
      )
    case 'multi_select': {
      const picked = Array.isArray(draft) ? draft : []
      return (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={field.label}>
          {optionsFor(picklists, field.picklistId, keep).map(o => {
            const on = picked.includes(o.value)
            return (
              <Chip
                key={o.id}
                selected={on}
                disabled={disabled}
                onClick={() => onChange(on ? picked.filter(v => v !== o.value) : [...picked, o.value])}
              >
                {o.label}
              </Chip>
            )
          })}
        </div>
      )
    }
    case 'user':
      return (
        <select value={text} disabled={disabled} onChange={e => onChange(e.target.value)} aria-label={field.label}>
          <option value="">— ingen —</option>
          {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          {text && !people.some(p => p.id === text) && <option value={text}>Tidigare användare</option>}
        </select>
      )
    case 'date':
      return <input type="date" value={text} disabled={disabled} onChange={e => onChange(e.target.value)} aria-label={field.label} />
    case 'number':
      return <input inputMode="decimal" value={text} disabled={disabled} onChange={e => onChange(e.target.value)} aria-label={field.label} />
    case 'url':
      return <input type="url" placeholder="https://" value={text} disabled={disabled} onChange={e => onChange(e.target.value)} aria-label={field.label} />
    default:
      return <input value={text} disabled={disabled} onChange={e => onChange(e.target.value)} aria-label={field.label} />
  }
}

const OUTCOME_TONE: Record<Deal['outcome'], string> = {
  open: 'bg-ink-50 text-ink-700 border-ink-200',
  won: 'bg-[var(--color-success)]/10 text-[var(--color-success)] border-[var(--color-success)]/30',
  lost: 'bg-[var(--color-danger)]/10 text-[var(--color-danger)] border-[var(--color-danger)]/30',
}
const OUTCOME_LABEL: Record<Deal['outcome'], string> = { open: 'Öppen', won: 'Vunnen', lost: 'Förlorad' }

function DealList({ deals }: { deals: Deal[] }) {
  if (!deals.length) return <p className="text-xs text-ink-500 m-0 mb-3">Inga affärer än.</p>
  return (
    <ul className="list-none m-0 p-0">
      {deals.map(d => (
        <li key={d.id} className="grid grid-cols-[1fr_auto] gap-3 py-2 items-center border-b border-ink-100 last:border-b-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[13px] text-ink-1000">
              <span
                aria-hidden="true"
                className={cls('inline-block w-2 h-2 rounded-full shrink-0', !d.stage.color && 'bg-ink-300')}
                style={d.stage.color ? { background: d.stage.color } : undefined}
              />
              <span className="font-medium">{d.stage.label}</span>
              <span className="text-ink-500">· {d.pipeline.name}</span>
            </div>
            <div className="text-[11px] text-ink-500 mt-0.5 tabular-nums">
              Öppnad {fmtDate(d.openedAt)}
              {d.closedAt && ` · stängd ${fmtDate(d.closedAt)}`}
              {d.value != null && ` · ${fmtNum(d.value)}${d.currency ? ` ${d.currency}` : ''}`}
              {d.lostReason && ` · ${d.lostReason.label}`}
            </div>
          </div>
          <span className={cls('text-[11px] font-medium px-2 py-0.5 rounded-full border', OUTCOME_TONE[d.outcome])}>
            {OUTCOME_LABEL[d.outcome]}
          </span>
        </li>
      ))}
    </ul>
  )
}

function OpenDeal({ companyId, deals, pipelines }: {
  companyId: string
  deals: Deal[]
  pipelines: PipelineDef[]
}) {
  const open = useOpenDeal(companyId)
  const canEdit = useCanEdit()
  const taken = pipelinesWithOpenDeal(deals)
  const free = pipelines.filter(p => !taken.has(p.id))
  const [pipelineId, setPipelineId] = useState('')
  if (!free.length) return null
  const chosen = free.find(p => p.id === pipelineId) ?? free.find(p => p.isDefault) ?? free[0]

  return (
    <div className="flex flex-wrap items-center gap-2 pt-3">
      {free.length > 1 && (
        <Select
          aria-label="Pipeline för den nya affären"
          className="py-1 text-xs"
          value={chosen.id}
          onChange={e => setPipelineId(e.target.value)}
        >
          {free.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      )}
      <Button
        size="sm"
        disabled={open.isPending || !canEdit}
        title={canEdit ? undefined : READ_ONLY_TITLE}
        onClick={() => open.mutate({ pipelineId: chosen.id })}
      >
        {open.isPending ? 'Öppnar…' : `Öppna en ny affär${free.length === 1 ? ` i ${chosen.name}` : ''}`}
      </Button>
      {open.error && (
        <span role="alert" className="text-xs text-[var(--color-danger)]">{(open.error as Error).message}</span>
      )}
    </div>
  )
}
